import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, Empty, Spin } from "antd";
import { fetchZendeskCause } from "util/zendeskAppApi";
import { ChangeChip, DailyBars } from "./parts";
import { SENTIMENTS, channelKey, sentimentShares, timeAgo } from "./zendeskFormat";
import styles from "./style.module.scss";

const SENTIMENT_COLORS = { negative: "#e11d48", neutral: "#f59e0b", positive: "#10b981" };

/** One root cause: this week vs last week, 14-day trend, sentiment split and the latest tickets in Zendesk. */
const CauseDrawer = ({ causeId, subdomain, onClose }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!causeId) {
      setData(null);
      return undefined;
    }
    let live = true;
    setLoading(true);
    setFailed(false);
    fetchZendeskCause(causeId, subdomain).then((res) => {
      if (!live) return;
      if (!res || res.error || res.cancel) {
        setFailed(true);
        setData(null);
      } else {
        setData(res);
      }
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [causeId, subdomain]);

  const cause = data?.cause;
  const shares = sentimentShares(cause?.sentiment);

  return (
    <Drawer open={Boolean(causeId)} onClose={onClose} width={560} destroyOnClose title={null}>
      {loading ? (
        <div className={styles.loading}>
          <Spin />
        </div>
      ) : null}
      {!loading && failed ? <Empty description={t("zendeskPage.cause_not_found")} /> : null}
      {!loading && cause ? (
        <div className={styles.drawer}>
          <div className={styles.eyebrow}>{t("zendeskPage.root_cause")}</div>
          <h3 className={styles.drawerTitle}>{cause.label}</h3>
          <div className={styles.tag}>{cause.tag}</div>

          <div className={styles.mini}>
            <div className={styles.kpi}>
              <div className={styles.kpiValue}>{cause.this_week}</div>
              <div className={styles.kpiLabel}>{t("zendeskPage.this_week_lower")}</div>
            </div>
            <div className={styles.kpi}>
              <div className={styles.kpiValue}>{cause.last_week}</div>
              <div className={styles.kpiLabel}>{t("zendeskPage.last_week_lower")}</div>
            </div>
            <div className={styles.kpi}>
              <div className={styles.kpiValue}>
                <ChangeChip changePct={cause.change_pct} />
              </div>
              <div className={styles.kpiLabel}>{t("zendeskPage.change")}</div>
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t("zendeskPage.last_14_days")}</h2>
            <DailyBars values={cause.daily_14d || []} height={60} compact />
            <h2 className={`${styles.cardTitle} ${styles.sentimentTitle}`}>{t("zendeskPage.sentiment")}</h2>
            {shares ? (
              <>
                <div className={styles.sentimentBar}>
                  {SENTIMENTS.map((k) =>
                    shares[k] ? <span key={k} style={{ width: `${shares[k]}%`, background: SENTIMENT_COLORS[k] }} /> : null
                  )}
                </div>
                <div className={styles.legend}>
                  {SENTIMENTS.map((k) => (
                    <span key={k}>
                      <i style={{ background: SENTIMENT_COLORS[k] }} />
                      {t(`zendeskPage.share_${k}`, { pct: shares[k] })}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <div className={styles.muted}>–</div>
            )}
          </div>

          <h2 className={`${styles.cardTitle} ${styles.ticketsTitle}`}>{t("zendeskPage.latest_tickets")}</h2>
          {(data.tickets || []).map((tk) => (
            <div key={tk.ticket_id} className={styles.ticket}>
              <div>
                <div className={styles.ticketSubject}>{tk.subject || t("zendeskPage.no_subject")}</div>
                <div className={styles.ticketMeta}>
                  <span className={styles.dot} style={{ background: SENTIMENT_COLORS[tk.sentiment] || "#9ca3af" }} />
                  {t(`zendeskPage.sentiment_${SENTIMENT_COLORS[tk.sentiment] ? tk.sentiment : "neutral"}`)} ·{" "}
                  {t(`zendeskPage.channel_${channelKey(tk.channel)}`)} · {timeAgo(tk.created_at, i18n.language)}
                </div>
              </div>
              <a className={styles.ticketLink} href={tk.url} target="_blank" rel="noopener noreferrer">
                {t("zendeskPage.in_zendesk", { id: tk.ticket_id })} ↗
              </a>
            </div>
          ))}
          <div className={styles.footnote}>{t("zendeskPage.latest_note", { count: (data.tickets || []).length })}</div>
        </div>
      ) : null}
    </Drawer>
  );
};

export default CauseDrawer;
